"use client";
import Icon from "@/components/icons/Icons";
import CustomModal from "@/components/modal/CustomModal";
import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import CustomCalendar from "@/components/ui/CustomCalendar";
import CustomDropdown from "@/components/ui/CustomDropdown";
import useScrollTrigger from "@/utils/useScrollTrigger";
import { apiPost } from "@/utils/apiClient";
import { addToast } from "@/components/ui/CustomToastManager";
import { useLoginModal } from "@/utils/useLoginModal";

type SettingsTabProps = {
  schemeName: string | null | undefined;
  price: string;
  setPrice: React.Dispatch<React.SetStateAction<string>>;
  conditionCode: number;
  setConditionCode: React.Dispatch<React.SetStateAction<number>>;
  expiration: string;
  setExpiration: React.Dispatch<React.SetStateAction<string>>;
  triggerType: "once" | "every";
  setTriggerType: React.Dispatch<React.SetStateAction<"once" | "every">>;
  errors: {
    price: string;
    expiration: string;
  };
};

type APIResponse = {
  status: boolean;
  message: string;
};

type NotificationTypes = {
  mail: boolean;
  app: boolean;
  chat: boolean;
};

type NotificationTabProps = {
  notifications: {
    mail: boolean;
    app: boolean;
    chat: boolean;
  };
  toggleNotification: (key: keyof NotificationTypes) => void;
  errors: {
    notification: string;
  };
  message: string;
};

const CONDITIONS = [
  { id: 1, value: "Crossing" },
  { id: 2, value: "Crossing up" },
  { id: 3, value: "Crossing down" },
  { id: 4, value: "Less than" },
  { id: 5, value: "Greater than" },
];

type PopupProps = {
  schemeCode?: string;
  schemeName?: string | null | undefined;
  defaultPrice?: number;
};

export default function AlertsAndTriggers({ schemeCode, schemeName, defaultPrice }: PopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"settings" | "notification">("settings");
  const [price, setPrice] = useState<string>(defaultPrice ? (defaultPrice + 5).toFixed(2) : "100.00");
  const [conditionCode, setConditionCode] = useState<number>(1);
  const [expiration, setExpiration] = useState(format(new Date(), "yyyy-MM-dd"));
  const [triggerType, setTriggerType] = useState<"once" | "every">("once");
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState({
    mail: true,
    app: true,
    chat: true,
  });

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const contentHeight = activeTab === "settings" ? 312 : 218;

  const message = `${schemeName} is Crossing ₹${price}`;

  const scrollDown = useScrollTrigger(100);

  const [errors, setErrors] = useState({
    price: "",
    expiration: "",
    message: "",
    notification: "",
  });

  const toggleNotification = (key: keyof typeof notifications) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const validate = () => {
    let hasError = false;
    const newErrors = { price: "", expiration: "", message: "", notification: "" };

    if (!price || price == "0" || isNaN(Number(price))) {
      newErrors.price = "Please enter a valid number";
      hasError = true;
    }

    if (!expiration) {
      newErrors.expiration = "Please select an expiration date";
      hasError = true;
    }

    if (!notifications.mail && !notifications.app && !notifications.chat) {
      newErrors.notification = "Please select at least one notification method";
      hasError = true;
    }
    setErrors(newErrors);
    return !hasError;
  };

  const onCreate = async () => {
    if (!validate()) return;
    setLoading(true);

    const mailStatus = notifications.mail ? 1 : 0;
    const notiStatus = notifications.app ? 1 : 0;
    const chatStatus = notifications.chat ? 1 : 0;

    const payload = {
      scheme_name: schemeName,
      scheme_code: schemeCode,
      price: Number(price),
      condition: conditionCode,
      trigger_frequency: triggerType,
      trigger_expire: 1,
      mail_status: mailStatus,
      noti_status: notiStatus,
      whatsapp_status: chatStatus,
      expire_date: expiration,
    };

    try {
      const res: APIResponse = await apiPost("data/updateNavTriggers", payload);

      if (!res.status) {
        setLoading(false);

        addToast({
          title: "",
          description: "Failed to create trigger!",
          status: "failed",
        });
        throw new Error("Failed to create trigger");
      }
      addToast({
        title: "",
        description: "Trigger created successfully!",
        status: "success",
      });
      setLoading(false);
      setIsOpen(false);
    } catch (err) {
      setLoading(false);
      addToast({
        title: "",
        description: "Failed to create trigger!",
        status: "failed",
      });
      console.error("Error creating trigger:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = () => {
    if (isLoggedIn) {
      onCreate();
    } else {
      open();
      setModalKey("alert-fund");
    }
  };

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <button onClick={() => setIsOpen(true)} aria-label="Showing Alerts" className={`sm:p-1.5 mx-auto sm:mx-0 shrink-0 inline-flex sm:inline-block justify-center fixed sm:static ${scrollDown ? "bottom-70 sm:bottom-53.75" : "bottom-53.75 sm:bottom-36.25"} opacity-100 right-4 w-11.5 h-11.5 sm:w-16 sm:h-16 rounded-full transition-all duration-300 z-150 sm:z-1`}>
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-11.5 h-11.5 sm:w-full sm:h-full shadow-[0_4px_4px_0_#1A51AA20] sm:shadow-none rounded-full transition duration-350 text-[#292D32] hover:text-primaryBlue">
          <rect x="0.5" y="0.5" width="49" height="49" rx="24.5" fill="white" />
          <rect x="0.5" y="0.5" width="49" height="49" rx="24.5" stroke="currentColor" strokeOpacity={0.4} />
          <path
            d="M32.3399 27.49L31.3399 25.83C31.1299 25.46 30.9399 24.76 30.9399 24.35V21.82C30.9399 19.47 29.5599 17.44 27.5699 16.49C27.0499 15.57 26.0899 15 24.9899 15C23.8999 15 22.9199 15.59 22.3999 16.52C20.4499 17.49 19.0999 19.5 19.0999 21.82V24.35C19.0999 24.76 18.9099 25.46 18.6999 25.82L17.6899 27.49C17.2899 28.16 17.1999 28.9 17.4499 29.58C17.6899 30.25 18.2599 30.77 18.9999 31.02C20.9399 31.68 22.9799 32 25.0199 32C27.0599 32 29.0999 31.68 31.0399 31.03C31.7399 30.8 32.2799 30.27 32.5399 29.58C32.7999 28.89 32.7299 28.13 32.3399 27.49Z"
            fill="currentColor"
          />
          <path d="M27.8302 33.01C27.4102 34.17 26.3002 35 25.0002 35C24.2102 35 23.4302 34.68 22.8802 34.11C22.5602 33.81 22.3202 33.41 22.1802 33C22.3102 33.02 22.4402 33.03 22.5802 33.05C22.8102 33.08 23.0502 33.11 23.2902 33.13C23.8602 33.18 24.4402 33.21 25.0202 33.21C25.5902 33.21 26.1602 33.18 26.7202 33.13C26.9302 33.11 27.1402 33.1 27.3402 33.07C27.5002 33.05 27.6602 33.03 27.8302 33.01Z" fill="currentColor" />
        </svg>
      </button>

      <CustomModal isOpen={isOpen} onClose={handleCloseModal} showCloseButton={false} maxSize="max-w-500" className={`bg-white rounded-t-2xl sm:rounded-4xl p-5 xs:p-7 transition-all duration-350 overflow-hidden transform`}>
        <div className="text-xl font-semibold text-primaryBlack text-center xs:mb-4 flex items-center justify-between">
          Triggers and Alerts
          <button onClick={handleCloseModal} className="border rounded-full p-1 transition duration-300 border-black/55 hover:border-black">
            <Icon.popupCloseIcon className="w-3 h-3 rotate-45 transition duration-300 text-black/55 hover:text-black" />
          </button>
        </div>

        <div className="flex mb-6 max-w-fit mx-auto">
          {(["settings", "notification"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`py-2.5 px-3 text-sm font-medium transition duration-300 border-b-2  ${activeTab === tab ? "border-black text-black" : "text-gray-500 border-transparent hover:text-primaryBlack"}`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <motion.div animate={{ height: contentHeight }} transition={{ duration: 0.3 }} className="overflow-y-auto scrollbar-xs xs:overflow-hidden pr-1 xs:pr-0">
          <AnimatePresence mode="wait">
            {activeTab === "settings" && <SettingsTab schemeName={schemeName} price={price} setPrice={setPrice} conditionCode={conditionCode} setConditionCode={setConditionCode} expiration={expiration} setExpiration={setExpiration} triggerType={triggerType} setTriggerType={setTriggerType} errors={errors} />}

            {activeTab === "notification" && <NotificationTab notifications={notifications} toggleNotification={toggleNotification} errors={errors} message={message} />}
          </AnimatePresence>
        </motion.div>

        <div className="flex items-center gap-4 mt-5">
          <button onClick={handleCloseModal} className="flex-1 block text-primaryBlue border border-primaryBlue px-4 py-3 rounded-xl text-base font-semibold transition duration-300 hover:bg-[#EBF3FF]">
            Cancel
          </button>

          <button onClick={handleCreateAlert} className="flex-1 block bg-primaryBlue text-white px-4 py-3 rounded-xl text-base font-semibold transition duration-300 shine-button-hover">
            {loading ? "Creating..." : "Create"}
          </button>
        </div>
      </CustomModal>
    </>
  );
}

const SettingsTab: React.FC<SettingsTabProps> = ({ schemeName, price, setPrice, conditionCode, setConditionCode, expiration, setExpiration, triggerType, setTriggerType, errors }) => {
  const labelWidth = "w-28 shrink-0";
  const conditionLabel = CONDITIONS[conditionCode - 1];
  const [isConditionOpen, setIsConditionOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const conditionRef = useRef<HTMLButtonElement>(null);
  const selectedDate = expiration ? parseISO(expiration) : null;

  const handleIncreasePrice = () => {
    const current = parseFloat(price || "0");
    const newPrice = (current + 10).toFixed(2);
    setPrice(newPrice);
  };

  const handleDecreasePrice = () => {
    const current = parseFloat(price || "0");
    const decreased = current - 10;
    const newPrice = decreased <= 0 ? "0" : decreased.toFixed(2);
    setPrice(newPrice);
  };

  const handleInputPrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    const validRegex = /^\d*\.?\d*$/;
    if (!validRegex.test(input)) return;

    if (input.startsWith(".")) {
      input = "0" + input;
    }

    if (price === "0" && /^\d$/.test(input)) {
      setPrice(input);
      return;
    }

    if (price === "0" && input === "0.") {
      setPrice("0.");
      return;
    }

    if (/^0\d+/.test(input)) {
      input = input.replace(/^0+/, "");
    }

    setPrice(input);
  };

  return (
    <motion.div key="settings" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.25 }}>
      <div className="flex items-center mb-6">
        <p className={`${labelWidth} text-sm text-[#444444]`}>Fund</p>
        <p className="text-sm text-primaryBlue font-semibold line-clamp-1">{schemeName}</p>
      </div>

      <div className="flex items-center mb-6">
        <p className={`${labelWidth} text-sm text-[#444444]`}>Price</p>
        <div className="w-full relative group">
          <span className="absolute top-0 left-3 bottom-0 my-auto h-max text-sm text-primaryBlack font-medium">₹</span>
          <input type="text" inputMode="decimal" value={price} onChange={handleInputPrice} placeholder="Enter value" className="w-full bg-white border px-6 py-2 rounded-lg text-sm text-primaryBlack font-medium transition-all duration-200 border-[#1A51AA33] hover:border-[#1a51aa66]" />
          <span className="space-y-1 inline-flex flex-col absolute top-0 right-3 bottom-0 my-auto h-max transition duration-300 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto">
            <i onClick={handleIncreasePrice} className="inline-block align-middle w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-[#202020a8] cursor-pointer hover:border-b-primaryBlack" />
            <i onClick={parseFloat(price) <= 0 ? undefined : handleDecreasePrice} className={`inline-block align-middle w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] ${parseFloat(price) <= 0 ? "border-t-gray-300 cursor-not-allowed opacity-50 pointer-events-none" : "border-t-[#202020a8] cursor-pointer hover:border-t-primaryBlack"}`} />
          </span>
          {errors.price && <p className="absolute -bottom-4 text-xs text-red-500 mt-1">{errors.price}</p>}
        </div>
      </div>

      <div className="flex items-center mb-5">
        <p className={`${labelWidth} text-sm text-[#444444]`}>Condition</p>

        <div className="relative w-full">
          <button ref={conditionRef} onClick={() => setIsConditionOpen((prev) => !prev)} className={`w-full py-2 px-3 rounded-lg text-left bg-white border text-sm font-medium text-primaryBlack transition-all duration-200 flex items-center justify-between gap-2 ${isConditionOpen ? "border-[#1a51aa66]" : "border-[#1A51AA33] hover:border-[#1a51aa66]"}`}>
            {conditionLabel.value}
            <Icon.dropdownDownArrow className={`fill-primaryBlack transition duration-350 ${isConditionOpen ? "rotate-180" : ""}`} />
          </button>

          <CustomDropdown isOpen={isConditionOpen} onClose={() => setIsConditionOpen((prev) => !prev)} excludeRefs={[conditionRef]} className={`absolute top-full left-0 w-full mt-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 py-1 overflow-hidden`}>
            <div className="flex flex-col text-sm max-h-36 scrollbar-xs overflow-y-auto">
              {[...CONDITIONS]
                .sort((a, b) => (a.id === conditionCode ? -1 : b.id === conditionCode ? 1 : 0))
                .map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setConditionCode(option.id);
                      setIsConditionOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 transition-colors duration-200 ${conditionCode === option.id ? "bg-[#f0f5fd] text-primaryBlue font-medium" : "hover:bg-[#f0f5fd] text-[#333]"}`}
                  >
                    {option.value}
                  </button>
                ))}
            </div>
          </CustomDropdown>
        </div>
      </div>

      <div className="flex items-start mb-5">
        <p className={`${labelWidth} text-sm text-[#444444] my-3`}>Trigger</p>
        <div>
          <span className="flex gap-4 mt-1">
            {[
              { label: "Only Once", value: "once" },
              { label: "Every Time", value: "every" },
            ].map(({ label, value }) => (
              <button key={value} onClick={() => setTriggerType(value as "once" | "every")} className={`py-2 text-sm border-b-2 transition duration-300 ${triggerType === value ? "border-black text-black" : "border-transparent text-gray-500 hover:text-primaryBlack"}`}>
                {label}
              </button>
            ))}
          </span>

          <p className="text-xs text-[#6E6E73] mt-4 h-8">{triggerType === "every" ? "The alert will trigger every time the condition is met, but not more than 1 time per minute" : "The alert will only trigger once and will not be repeated"}</p>
        </div>
      </div>

      <div className="flex items-center relative">
        <p className={`${labelWidth} text-sm text-[#444444]`}>Expiration</p>

        <div className="relative w-full">
          <button ref={buttonRef} onClick={() => setIsCalendarOpen((prev) => !prev)} className={`w-full py-2 px-3 rounded-lg text-left bg-white border text-sm font-medium text-primaryBlack transition-all duration-200 flex items-center justify-between gap-2 ${isCalendarOpen ? "border-[#1a51aa66]" : "border-[#1A51AA33] hover:border-[#1a51aa66]"}`}>
            {selectedDate ? format(selectedDate, "dd MMM yyyy") : "Select date"}
            <Icon.dropdownDownArrow className={`fill-primaryBlack transition duration-350 ${isCalendarOpen ? "rotate-180" : ""}`} />
          </button>

          <CustomDropdown isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} className="absolute bottom-full left-0 w-full mb-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 p-1.5 overflow-hidden" excludeRefs={[buttonRef]}>
            <CustomCalendar
              defaultSelectedDate={new Date()}
              onDateSelect={(date) => {
                setExpiration(format(date, "yyyy-MM-dd"));
                setIsCalendarOpen(false);
              }}
              minSelectableDate={new Date()}
              fontSizeBase={false}
            />
          </CustomDropdown>

          {errors.expiration && <p className="absolute -bottom-4 text-xs text-red-500">{errors.expiration}</p>}
        </div>
      </div>
    </motion.div>
  );
};

const NotificationTab: React.FC<NotificationTabProps> = ({ notifications, toggleNotification, errors, message }) => {
  const labelWidth = "w-28 shrink-0";

  return (
    <motion.div key="notification" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.25 }} className="space-y-4">
      <div className="space-y-5.5 relative">
        {(
          [
            { p: "Send by email", key: "mail" },
            { p: "Notify in app", key: "app" },
            { p: "Send on WhatsApp", key: "chat" },
          ] as const
        ).map((n) => (
          <label key={n.key} className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={notifications[n.key]} onChange={() => toggleNotification(n.key)} className="accent-blue-600 w-4 h-4" />
            <p className="text-sm">{n.p}</p>
          </label>
        ))}

        {errors.notification && <p className="absolute -bottom-4.5 text-xs text-red-500">{errors.notification}</p>}
      </div>

      <div className="relative">
        <p className={`${labelWidth} text-sm text-[#444444]`}>Message</p>
        <textarea rows={3} value={message} readOnly className="w-full border px-3 py-2 rounded-lg text-sm text-primaryBlack font-medium resize-none" />
      </div>
    </motion.div>
  );
};
